/**
 * Storage Abstraction for Safari Private Browsing Compatibility
 * 
 * Safari Private Browsing throws QuotaExceededError when accessing localStorage.
 * This utility provides a fallback to sessionStorage, which works in private browsing.
 * 
 * Trade-off: In Safari Private Browsing, data won't persist after tab close.
 * This is acceptable and expected behavior for private browsing.
 */

// Check if localStorage is available (fails in Safari Private Browsing)
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__storage_test__';
    localStorage.setItem(testKey, '1');
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

// Cache the result to avoid repeated checks
const canUseLocalStorage = isLocalStorageAvailable();

/**
 * Safe storage wrapper that falls back to sessionStorage when localStorage is unavailable.
 * Use this instead of direct localStorage calls throughout the app.
 */
export const storage = {
  /**
   * Get an item from storage.
   * Checks localStorage first, then sessionStorage as fallback.
   */
  getItem(key: string): string | null {
    try {
      if (canUseLocalStorage) {
        return localStorage.getItem(key);
      }
      return sessionStorage.getItem(key);
    } catch {
      // Final fallback if even sessionStorage fails
      try {
        return sessionStorage.getItem(key);
      } catch {
        return null;
      }
    }
  },

  /**
   * Set an item in storage.
   * Tries localStorage first, falls back to sessionStorage.
   */
  setItem(key: string, value: string): void {
    try {
      if (canUseLocalStorage) {
        localStorage.setItem(key, value);
      } else {
        sessionStorage.setItem(key, value);
      }
    } catch {
      // Final fallback if even sessionStorage fails
      try {
        sessionStorage.setItem(key, value);
      } catch {
        // Silently fail - nothing we can do
        console.warn(`[Storage] Unable to save ${key} - storage unavailable`);
      }
    }
  },

  /**
   * Remove an item from storage.
   * Removes from both localStorage and sessionStorage to ensure cleanup.
   */
  removeItem(key: string): void {
    try {
      if (canUseLocalStorage) {
        localStorage.removeItem(key);
      }
    } catch {
      // Ignore localStorage errors
    }
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Ignore sessionStorage errors
    }
  },

  /**
   * Check if we're using the fallback (sessionStorage) mode.
   * Useful for showing users a warning about data not persisting.
   */
  isUsingFallback(): boolean {
    return !canUseLocalStorage;
  },
};

export default storage;
